import React from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { HttpProject } from "tekkr-common/dist/model/project/project";
import { ProjectWorkstreamSubpage } from "./project-workstream-subpage";
import { ProjectDeliverableSubpage } from "./project-deliverable-subpage";
import { ProjectOverviewSubpage } from "./project-overview-subpage";
import { NetworkIcon } from "lucide-react";
import AnimateHeight from "react-animate-height";
import { cn } from "../../lib/utils";
import { ProjectProvider, useProject } from "../../data/project";
import { Button } from "../../components/ui/button";
import { ProjectWorkstreamIcon } from "../../components/shared/project/workstream-header";
import { DeliverableIcon } from "../../components/shared/project/deliverable-card";
import { useSetProjectChatContext } from "./components/project-chat";

function Spacer(props: React.PropsWithChildren<{ width: "full" | "narrow" }>) {
    return (
        <div className={"flex w-full max-w-[100vw] flex-row overflow-hidden"}>
            <Sidebar />
            <div className={"flex grow flex-col items-center py-6"}>
                <div
                    className={cn(
                        "mx-auto flex w-full flex-col items-stretch gap-4 lg:gap-8",
                        props.width === "full" ? "max-w-[90%]" : "max-w-4xl px-8 xl:py-6"
                    )}
                >
                    {props.children}
                </div>
            </div>
        </div>
    );
}

function useParamNav() {
    const KEY_WS_ID = "wsId";
    const KEY_DELIVERABLE_ID = "dId";
    const [searchParams, setSearchParams] = useSearchParams();
    const workstreamId = searchParams.get(KEY_WS_ID);
    const deliverableId = searchParams.get(KEY_DELIVERABLE_ID);
    function snapshot(sp: URLSearchParams) {
        return JSON.stringify([sp.get(KEY_WS_ID), sp.get(KEY_DELIVERABLE_ID)]);
    }
    function updateSearchParams(fn: (sp: URLSearchParams) => void, replace = false) {
        const sp = searchParams;
        const before = snapshot(sp);
        fn(sp);
        if (snapshot(sp) !== before) {
            setSearchParams(sp, { replace });
        }
    }
    function setWorkstreamId(id: string | null, replace = false) {
        updateSearchParams((sp) => {
            if (id) {
                sp.set(KEY_WS_ID, id);
            } else {
                sp.delete(KEY_WS_ID);
            }
            sp.delete(KEY_DELIVERABLE_ID);
        }, replace);
    }
    function setDeliverableId(id: string | null, replace = false) {
        updateSearchParams((sp) => {
            if (id) {
                sp.set(KEY_DELIVERABLE_ID, id);
            } else {
                sp.delete(KEY_DELIVERABLE_ID);
            }
        }, replace);
    }

    return { deliverableId, workstreamId, setDeliverableId, setWorkstreamId };
}

function ProjectPageContent() {
    const { project } = useProject();
    const navigate = useNavigate();
    const { deliverableId, workstreamId, setDeliverableId, setWorkstreamId } = useParamNav();

    const staleParamsRedirect = getRedirectPathForStaleParams(project, workstreamId, deliverableId);
    if (staleParamsRedirect) {
        navigate(staleParamsRedirect, { replace: true });
        return null;
    }

    const isAligned = project.tasks?.length > 0;
    if (!isAligned) {
        navigate(`/project/${project.id}/plan-alignment`, { replace: true });
        return null;
    }

    if (!workstreamId) {
        const onSelected = (wsId: string, deliverableId?: string) => {
            setWorkstreamId(wsId);
            if (deliverableId) {
                setDeliverableId(deliverableId, true);
            }
        };
        return (
            <Spacer width={"full"}>
                <ProjectOverviewSubpage onSelected={onSelected} />
            </Spacer>
        );
    }

    return (
        <Spacer width={"narrow"}>
            <div className={"flex flex-col items-stretch overflow-y-scroll"}>
                {deliverableId ? (
                    <ProjectDeliverableSubpage
                        workstreamId={workstreamId}
                        deliverableId={deliverableId}
                        onUnselectDeliverable={() => setDeliverableId(null)}
                    />
                ) : (
                    <ProjectWorkstreamSubpage
                        key={project.id + "-" + workstreamId}
                        workstreamId={workstreamId}
                        onSelectDeliverable={setDeliverableId}
                    />
                )}
            </div>
        </Spacer>
    );
}

export function ProjectPage() {
    const params = useParams();
    const projectId = params.id!;
    return (
        <ProjectProvider projectId={projectId}>
            <ProjectPageContent />
        </ProjectProvider>
    );
}

function useChatContext(nav: ReturnType<typeof useParamNav>) {
    const setChatContext = useSetProjectChatContext();
    if (!nav.deliverableId && !nav.workstreamId) {
        setChatContext(null);
    } else if (!nav.deliverableId) {
        setChatContext({ type: "workstream", id: nav.workstreamId! });
    } else {
        setChatContext({ type: "deliverable", workstreamId: nav.workstreamId!, id: nav.deliverableId });
    }
}

function Sidebar() {
    const { project } = useProject();
    const nav = useParamNav();
    useChatContext(nav);

    const barWidthClass = "w-[25vw] min-w-64 max-w-96";

    return (
        <div
            className={cn(
                barWidthClass,
                "shrink-0 border-r transition-all",
                !nav.workstreamId && "!min-w-0 !max-w-0 overflow-hidden"
            )}
        >
            <div className={cn(barWidthClass, "my-2")}>
                <div className={"flex w-full flex-col items-start gap-2 p-5"}>
                    <h4>{project.name}</h4>
                    <Button
                        onClick={() => nav.setWorkstreamId(null)}
                        size={"sm2"}
                        variant={"outline"}
                        className={"self-start border-foreground/30 bg-transparent text-sm hover:bg-background/20"}
                    >
                        <NetworkIcon className={"me-2 h-4 w-4"} />
                        Project overview
                    </Button>
                    <div className={"mt-5 text-sm font-medium text-foreground/50"}>Workstreams</div>
                    <div className={"flex flex-col gap-0.5"}>
                        {project.outline.workstreams.map((ws, wsIndex) => (
                            <React.Fragment key={ws.id}>
                                <div
                                    onClick={() => nav.setWorkstreamId(ws.id)}
                                    className={cn(
                                        "flex cursor-pointer flex-row items-center gap-2 rounded-md p-2 text-sm font-medium transition-all hover:bg-foreground/5",
                                        ws.id === nav.workstreamId && !nav.deliverableId && "!bg-foreground/10"
                                    )}
                                >
                                    <ProjectWorkstreamIcon ws={ws} index={wsIndex} size={"xs"} />
                                    {ws.name}
                                </div>
                                <AnimateHeight height={ws.id === nav.workstreamId && nav.deliverableId ? "auto" : 0}>
                                    <div className={"mb-2 mt-1 flex flex-col gap-1 ps-3"}>
                                        {ws.deliverables.map((deliverable, dIndex) => (
                                            <div
                                                onClick={() => nav.setDeliverableId(deliverable.id)}
                                                key={ws.id + "." + deliverable.id}
                                                className={cn(
                                                    "flex cursor-pointer flex-row items-center gap-2 rounded-md px-2 py-1 text-sm font-medium text-foreground/80 transition-all hover:bg-foreground/5",
                                                    deliverable.id === nav.deliverableId && "bg-foreground/10"
                                                )}
                                            >
                                                <DeliverableIcon
                                                    deliverableId={deliverable.id}
                                                    workstreamIndex={wsIndex}
                                                    deliverableIndex={dIndex}
                                                    size={"xs"}
                                                />
                                                {deliverable.name}
                                            </div>
                                        ))}
                                    </div>
                                </AnimateHeight>
                            </React.Fragment>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}

function getRedirectPathForStaleParams(
    project: HttpProject,
    workstreamId: string | null,
    deliverableId: string | null
): string | null {
    const workstream = workstreamId ? project.outline.workstreams.find((ws) => ws.id === workstreamId) : null;

    const deliverable =
        workstream && deliverableId ? workstream.deliverables.find((d) => d.id === deliverableId) : null;

    if (workstreamId && !workstream) {
        return `/project/${project.id}`;
    }

    if (deliverableId && !deliverable) {
        return `/project/${project.id}?wsId=${workstreamId}`;
    }

    return null;
}
